import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import { ArrowLeft, Save } from 'lucide-react';
import { useAppStore } from '../../../lib/store';
import { invoicesApi } from '../../../lib/api';
import { isRTL } from '../../../lib/i18n';

export default function InvoiceEdit() {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const user = useAppStore((state) => state.user);
  const language = useAppStore((state) => state.settings.language);

  // Form state - only editable fields that exist in schema
  // Schema: id, invoice_number, property_id, tenant_id, amount, vat_amount, total_amount,
  //         issue_date, due_date, status, description, created_at, updated_at
  const [formData, setFormData] = useState({
    invoice_number: '',
    amount: 0,
    vat_amount: 0,
    total_amount: 0,
    issue_date: '',
    due_date: '',
    status: 'draft' as 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled',
    description: '',
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load invoice data
  useEffect(() => {
    if (id) {
      loadInvoice();
    }
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await invoicesApi.getById(id!);

      if (response.error) {
        throw new Error(response.error.message || 'Failed to load invoice');
      }

      if (response.data) {
        const invoice = response.data;
        setFormData({
          invoice_number: invoice.invoice_number || '',
          amount: invoice.amount || 0,
          vat_amount: invoice.vat_amount || 0,
          total_amount: invoice.total_amount || 0,
          issue_date: invoice.issue_date ? invoice.issue_date.split('T')[0] : '',
          due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
          status: invoice.status || 'draft',
          description: invoice.description || '',
        });
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate totals when amount changes
  const handleAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    const vatRate = 0.15; // 15% VAT
    const vatAmount = amount * vatRate;
    const totalAmount = amount + vatAmount;

    setFormData((prev) => ({
      ...prev,
      amount,
      vat_amount: vatAmount,
      total_amount: totalAmount,
    }));
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const validateForm = (): boolean => {
    if (!formData.amount || formData.amount <= 0) {
      setError(t('common:required', 'Amount is required'));
      return false;
    }

    if (!formData.issue_date) {
      setError(t('common:required', 'Issue date is required'));
      return false;
    }

    if (!formData.due_date) {
      setError(t('common:required', 'Due date is required'));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    if (!user) {
      setError(t('common:auth.sessionExpired'));
      return;
    }

    setSubmitting(true);
    try {
      // Only update fields that exist in the schema
      const updateData = {
        amount: formData.amount,
        vat_amount: formData.vat_amount,
        total_amount: formData.total_amount,
        issue_date: formData.issue_date,
        due_date: formData.due_date,
        status: formData.status,
        description: formData.description,
      };

      const response = await invoicesApi.update(id!, updateData);

      if (response.error) {
        throw new Error(response.error.message || 'Failed to update invoice');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/dashboard/finance/invoices/${id}`);
      }, 1500);
    } catch (err: any) {
      console.error('Error updating invoice:', err);
      setError(err.message || 'Failed to update invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box dir={language === 'ar' ? 'rtl' : 'ltr'} sx={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          {...(isRTL()
            ? { endIcon: <ArrowLeft size={20} /> }
            : { startIcon: <ArrowLeft size={20} /> }
          )}
          onClick={() => navigate(`/dashboard/finance/invoices/${id}`)}
        >
          {t('common:back')}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {t('editInvoice', 'Edit Invoice')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formData.invoice_number}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('invoiceUpdatedSuccess', 'Invoice updated successfully!')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Invoice Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {t('vatInvoice', 'VAT Invoice')}
                </Typography>
                <Chip label={t('ksaVatCompliant', 'KSA VAT Compliant')} color="success" />
              </Box>

              <Grid container spacing={2}>
                {/* Invoice Number (read-only) */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('invoiceNumber', 'Invoice Number')}
                    value={formData.invoice_number}
                    disabled
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('status', 'Status')}
                    value={formData.status}
                    onChange={handleChange('status')}
                  >
                    <MenuItem value="draft">{t('invoiceStatuses.draft', 'Draft')}</MenuItem>
                    <MenuItem value="sent">{t('invoiceStatuses.sent', 'Sent')}</MenuItem>
                    <MenuItem value="paid">{t('invoiceStatuses.paid', 'Paid')}</MenuItem>
                    <MenuItem value="overdue">{t('invoiceStatuses.overdue', 'Overdue')}</MenuItem>
                    <MenuItem value="cancelled">{t('invoiceStatuses.cancelled', 'Cancelled')}</MenuItem>
                  </TextField>
                </Grid>

                {/* Issue Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('issueDate', 'Issue Date')}
                    value={formData.issue_date}
                    onChange={handleChange('issue_date')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-US' }}
                  />
                </Grid>

                {/* Due Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('dueDate', 'Due Date')}
                    value={formData.due_date}
                    onChange={handleChange('due_date')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-US' }}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('amount', 'Amount (Before VAT)')}
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                {/* VAT Amount (calculated) */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('vatAmount', 'VAT Amount (15%)')}
                    value={formatCurrency(formData.vat_amount)}
                    disabled
                  />
                </Grid>

                {/* Total Amount (calculated) */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('totalAmount', 'Total Amount')}
                    value={formatCurrency(formData.total_amount)}
                    disabled
                    sx={{
                      '& .MuiInputBase-input': {
                        fontWeight: 'bold',
                        color: 'primary.main',
                      },
                    }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('description', 'Description')}
                    value={formData.description}
                    onChange={handleChange('description')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/dashboard/finance/invoices/${id}`)}
              disabled={submitting}
            >
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Save size={20} />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? t('common:saving', 'Saving...') : t('common:save', 'Save Changes')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
